/**
 * Image optimization helper.
 *
 * Routes Supabase Storage public images through wsrv.nl — a free, global,
 * privacy-respecting image CDN that:
 *  - Converts PNG/JPG to WebP/AVIF on the fly (~70% smaller)
 *  - Resizes to the requested width (no overdraw)
 *  - Caches globally on Cloudflare's edge
 *
 * Public domain images (Unsplash, Pexels) and any non-Supabase URLs
 * are returned untouched.
 *
 * Usage:
 *   <img src={optimize(p.images[0], 320)} ... />
 */

const SUPABASE_HOST = 'uwqlciwlzwkyziurqzui.supabase.co';

/**
 * @param {string} src - original image URL
 * @param {number} [width=400] - target width in CSS px (will be 2x for retina)
 * @returns {string} - optimized URL
 */
export function optimize(src, width = 400) {
  if (!src) return src;

  // Skip non-Supabase URLs (Unsplash, Pexels already serve WebP)
  if (!src.includes(SUPABASE_HOST)) return src;

  // Skip data URLs and relative paths
  if (src.startsWith('data:') || src.startsWith('/')) return src;

  // Devicepixel ratio: serve 2x for sharp retina rendering
  const w = Math.round(width * 2);

  // wsrv.nl: ?url=...&w=W&output=webp&q=85
  // The "ssl:1" prefix tells wsrv to fetch over HTTPS.
  const cleanUrl = src.replace(/^https?:\/\//, '');
  return `https://wsrv.nl/?url=ssl:${encodeURIComponent(cleanUrl)}&w=${w}&output=webp&q=82&we`;
}

/**
 * Generate a srcset for responsive images.
 * @param {string} src
 * @param {number[]} widths
 */
export function srcset(src, widths = [200, 400, 600]) {
  if (!src || !src.includes(SUPABASE_HOST)) return undefined;
  return widths.map(w => `${optimize(src, w / 2)} ${w}w`).join(', ');
}
